import { api, apiUrl, type SpEntry } from './api'
import { reportError } from './errors'
import { freshToken } from './session-token'
import { clientHeaders } from './desktop'
import { noteFloorHeaders } from './client-floor'

/**
 * Authenticated BINARY downloads. Separate from lib/api.ts#request — which is JSON-only and ends
 * in res.json() — but resolving its URLs through the same apiUrl() base. That sharing is the whole
 * point of this module.
 *
 * The bug it exists to prevent: a relative '/v1/export?…' fetch in prod is answered by the Azure
 * Static Web App, not the backend. Its navigationFallback rewrites unknown paths to /index.html
 * and returns HTTP 200, so an `if (!res.ok)` guard sees success and the SPA cheerfully saves its
 * own 2 KB index.html as "<room>.pptx". Office then refuses to open it, and nothing anywhere
 * reports an error. Hence: one URL base, and a body that is checked before it is saved.
 */

export type ExportFormat = 'docx' | 'pptx' | 'md' | 'xlsx' | 'html'
export type ExportScope = 'part' | 'artefact' | 'bid'

/** OOXML containers are zip archives; every valid one opens with the local file header. */
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04] as const
const isZip = (h: Uint8Array): boolean => ZIP_MAGIC.every((b, i) => h[i] === b)

/** An EMPTY zip (no entries at all) opens straight on the end-of-central-directory record instead
 *  of a local file header — the shape a whole-bid export of a truly empty bid legitimately takes. */
const EOCD_MAGIC = [0x50, 0x4b, 0x05, 0x06] as const
const isZipContainer = (h: Uint8Array): boolean => isZip(h) || EOCD_MAGIC.every((b, i) => h[i] === b)

/**
 * Every Niobe-rendered HTML export carries this in its <head>, emitted by the RENDERER (not by the
 * corporate template) within the first kilobyte — backend/adapters/export/html.ts guarantees it and
 * a contract test asserts it. It is what tells a real export apart from the Static Web Apps shell,
 * which is also text/html with a 200.
 */
const NIOBE_HTML_MARKER = 'name="generator" content="Niobe'

/**
 * Positive body checks, per format. A format absent from the table is not checked — `md` is
 * legitimately arbitrary text. `head()` returning null (an environment that cannot read the blob)
 * always SKIPS the check: a sniff we could not perform must never block a good download.
 */
const BODY_CHECK: Record<string, { bytes: number; ok: (h: Uint8Array) => boolean; why: string }> = {
  docx: { bytes: 4, ok: isZip, why: 'not a valid Office document' },
  pptx: { bytes: 4, ok: isZip, why: 'not a valid Office document' },
  xlsx: { bytes: 4, ok: isZip, why: 'not a valid Office document' },
  html: {
    bytes: 1024,
    ok: (h) => new TextDecoder('utf-8').decode(h).includes(NIOBE_HTML_MARKER),
    why: 'not a Niobe HTML export',
  },
  // A whole-bid export (scope=bid) is ALWAYS this shape, whatever format the member picked for the
  // files inside it: one zip holding one rendered file per artefact. downloadExport() maps every
  // bid-scope request to this pseudo-format, so a request for e.g. format=html never runs the html
  // marker check against the outer zip bytes — that check would fail every time (the marker sits
  // inside a member's compressed bytes, never at the archive's own head).
  zip: { bytes: 4, ok: isZipContainer, why: 'not a valid zip archive' },
}

/**
 * Filename from a Content-Disposition header, if the browser can see one. The RFC 5987
 * `filename*=UTF-8''…` form wins when present: it is the only one that survives an accented export
 * name ("Réponse - v12.docx"), because the plain `filename=` the backend sends beside it is an
 * ASCII fold. This is what ends up on the anchor's `download`, so it IS the saved name.
 */
export function filenameOf(res: Response): string | null {
  const cd = res.headers.get('Content-Disposition')
  if (!cd) return null
  const ext = /filename\*=\s*UTF-8''([^;]+)/i.exec(cd)
  if (ext) {
    try {
      return decodeURIComponent(ext[1]!.trim())
    } catch {
      // Malformed percent-encoding — fall through to the plain form rather than throwing.
    }
  }
  const m = /filename="?([^";]+)"?/.exec(cd)
  return m ? m[1]!.trim() : null
}

/** First `n` bytes of a blob, or null when the environment cannot read them. */
async function head(blob: Blob, n: number): Promise<Uint8Array | null> {
  const slice = blob.slice(0, n)
  try {
    // Browsers have Blob.arrayBuffer(); jsdom (the test env) does not, but does implement
    // FileReader. If neither works we return null and the caller SKIPS the check — a sniff we
    // could not perform must never block a download that is otherwise fine.
    if (typeof slice.arrayBuffer === 'function') return new Uint8Array(await slice.arrayBuffer())
    return await new Promise<Uint8Array | null>((resolve) => {
      const r = new FileReader()
      r.onload = () => resolve(r.result instanceof ArrayBuffer ? new Uint8Array(r.result) : null)
      r.onerror = () => resolve(null)
      r.readAsArrayBuffer(slice)
    })
  } catch {
    return null
  }
}

/**
 * Hand a blob to the browser's downloader. Attach the anchor to the DOM before clicking (Firefox
 * ignores a detached one) and DEFER the revoke: revoking the object URL synchronously after
 * click() can truncate the in-flight blob read, which turns a binary .docx/.pptx/.xlsx into a
 * file Office reports as corrupt and cannot repair.
 */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

export interface DownloadOptions {
  /** Used when the server sends no Content-Disposition (or CORS hides it). */
  fallbackName: string
  /** Bearer token. Defaults to the live session token (renewed if it has expired). */
  token?: string
  /** When it names a zip-based Office format, the saved bytes are checked for 'PK\x03\x04'. */
  format?: string
}

/**
 * Fetch `path` from the backend with the session Bearer, verify the body really is the file the
 * server promised, then save it. Returns true on success; every failure is surfaced through the
 * global error popup, because a download that silently saves the wrong bytes is the whole bug.
 */
export async function downloadBinary(path: string, opts: DownloadOptions): Promise<boolean> {
  // Resolved here, not passed in from a component: an export fired after a long idle must not
  // ride the token the page happened to load with (see lib/session-token.ts).
  const token = opts.token ?? (await freshToken())
  const url = apiUrl(path)
  let res: Response
  try {
    // An export is a /v1 call like any other and is logged like any other, so it carries the same
    // client/version headers as lib/api.ts#request — this module bypasses that helper (it is
    // JSON-only) and would otherwise be the one authenticated call that says nothing about itself.
    res = await fetch(url, {
      headers: { ...clientHeaders(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
    noteFloorHeaders(res.headers)
  } catch {
    reportError('Cannot reach the server. Check that the backend is running.', `GET ${path}`)
    return false
  }
  if (!res.ok) {
    reportError(`The export failed (${res.status}).`, `GET ${path} → ${res.status}`)
    return false
  }
  // An HTML body on an export URL normally means the request never reached the API: the SPA host
  // answered it with the app shell (Static Web Apps navigationFallback) and called it 200.
  // Content-Type is CORS-safelisted, so this check works cross-origin with no backend change.
  // `format: 'html'` is the one legitimate case — and it is not waved through here, it is checked
  // HARDER below, on the body, by BODY_CHECK.html.
  if (/^text\/html\b/i.test(res.headers.get('Content-Type') ?? '') && opts.format !== 'html') {
    reportError(
      'The export did not come from the Niobe backend.',
      `GET ${url} returned an HTML page — the web host answered instead of the API. ` +
        'Check window.__NIOBE_CONFIG__.backendUrl in config.js.',
    )
    return false
  }
  const blob = await res.blob()
  const check = opts.format ? BODY_CHECK[opts.format] : undefined
  if (check) {
    const bytes = await head(blob, check.bytes)
    if (bytes && !check.ok(bytes)) {
      reportError(
        `The exported file is ${check.why}.`,
        `GET ${url} → ${blob.size} bytes failed the ${opts.format} body check`,
      )
      return false
    }
  }
  saveBlob(blob, filenameOf(res) ?? opts.fallbackName)
  return true
}

/**
 * `GET /v1/export` → a saved file. Bid scope keys off the bid id, the other scopes off the room
 * key, exactly as backend/inbound/http/routes/export.ts documents. The real name —
 * "<what was exported> - v<its version>" (backend/domain/export.ts#exportFilename) — arrives via
 * Content-Disposition, which is only readable cross-origin because the backend CORS layer lists it
 * in exposedHeaders. The fallback below is the last resort when it is not: the client cannot know
 * the stored version, so it keeps the old room-derived name.
 *
 * A whole-bid export is always a ZIP on the wire (one rendered file per deliverable), whatever
 * format was picked for the files inside it — `format` still selects that inner format for the
 * query string, but the body check and the fallback extension follow the actual wire shape.
 */
export function downloadExport(args: {
  room: string
  scope: ExportScope
  format: ExportFormat
  token?: string
}): Promise<boolean> {
  const { room, scope, format, token } = args
  const bid = room.split('/')[0] ?? ''
  const qs =
    scope === 'bid'
      ? `bid=${encodeURIComponent(bid)}&scope=bid&format=${format}`
      : `name=${encodeURIComponent(room)}&scope=${scope}&format=${format}`
  const wireFormat = scope === 'bid' ? 'zip' : format
  return downloadBinary(`/v1/export?${qs}`, {
    fallbackName: `${(scope === 'bid' ? bid : room).replace(/\//g, '_')}.${wireFormat}`,
    format: wireFormat,
    ...(token ? { token } : {}),
  })
}

/**
 * `POST /v1/bids/:bid/sharepoint/export` — the other half of the same action as
 * {@link downloadExport}: the same scope/format the member picked, but landing in the bid's own
 * SharePoint (its default `niobe/exports` folder, or wherever the destination picker points)
 * instead of the browser's downloads folder.
 *
 * Unlike the binary GET above this is a plain JSON POST (api.ts#request), so a refusal throws the
 * ordinary `ApiError` (lib/api.ts#isApiError) rather than a `GraphError` — ExportMenu renders it
 * inline exactly like any other bid-scoped write refusal (see BidSiteCard's `report()`).
 *
 * WHICH document to export travels in the body, and the caller must put it there: unlike
 * `GET /v1/export` — which derives the deliverable from the `name` room key
 * (backend/inbound/http/routes/export.ts#parseRoomKey) — this route passes `artefactId`/`name`
 * straight to `selectExportTargets`. See {@link ExportMenu}'s `selectionOf`.
 */
export function exportToSharePoint(
  bid: string,
  body: {
    scope: ExportScope
    format: ExportFormat
    /** Which deliverable, for `scope: 'artefact'`. */
    artefactId?: string
    /** The room key "<bid>/<artefact>/<part>", for `scope: 'part'`. */
    name?: string
    includeReviews?: boolean
    destination?: { driveId: string; itemId: string }
    /** The file's base name (no extension); omitted = the server's default name. */
    filename?: string
    /** `fail` throws a 409 `sharepoint_conflict` ApiError on a name already there; omitted, the server replaces. */
    conflict?: 'fail' | 'replace'
  },
): Promise<SpEntry> {
  return api.spExport(bid, body).then((r) => r.entry)
}
